/**
 * Entity list toolbar — visual parity with item-list Toolbar (§1E).
 *
 * On a phone it is one row — Filters, Sort, the count, View — and the rest
 * (group, summary, layout, columns, reports, density, select all) sits in the
 * View sheet (audit 2026-11-02, #46: the full row wrapped over five rows at
 * 375 px).
 */

import { useState, useCallback } from 'react';
import {
  SlidersHorizontal,
  Layers,
  ArrowUpDown,
  LayoutGrid,
  LayoutList,
  AlignJustify,
  ChevronDown,
  CheckSquare,
  BarChart3,
  FileText,
  Table2,
  LayoutDashboard,
  Settings2,
} from 'lucide-react';
import { ColumnsControl } from './ColumnPicker';
import { pluralizeEntityLabel } from './pluralizeEntityLabel';
import { useMobileExperience } from '../../mobile/featureFlag';
import { BottomSheet } from '../../mobile/BottomSheet';

export type EntityDensityMode = 'compact' | 'default' | 'comfortable';
export type EntityLayoutMode = 'table' | 'card';

export interface EntitySortOption {
  field: string;
  label: string;
}

export interface EntityGroupOption {
  field: string;
  label: string;
}

export interface EntityColumnOption {
  key: string;
  label: string;
}

export interface EntityListToolbarProps {
  density: EntityDensityMode;
  onDensityChange: (d: EntityDensityMode) => void;
  sortOptions: EntitySortOption[];
  sort: { field: string; direction: 'asc' | 'desc' };
  onSortChange: (sort: { field: string; direction: 'asc' | 'desc' }) => void;
  groupOptions?: EntityGroupOption[];
  groupBy: string | null;
  onGroupByChange: (field: string | null) => void;
  filterCount: number;
  onToggleFilterPanel: () => void;
  totalCount: number;
  filteredCount: number;
  selectedCount?: number;
  onSelectAllFiltered?: () => void;
  layoutMode?: EntityLayoutMode;
  onLayoutModeChange?: (mode: EntityLayoutMode) => void;
  showChart?: boolean;
  onToggleChart?: () => void;
  onManageReports?: () => void;
  columns?: EntityColumnOption[];
  visibleColumns?: string[];
  onToggleColumn?: (key: string) => void;
  onReorderColumns?: (order: string[]) => void;
  itemLabel?: string;
}

const DENSITY_MODES: EntityDensityMode[] = ['compact', 'default', 'comfortable'];
const DENSITY_ICON = { compact: AlignJustify, default: LayoutList, comfortable: LayoutGrid } as const;
const densityLabel = (mode: EntityDensityMode) => mode.charAt(0).toUpperCase() + mode.slice(1);

export function EntityListToolbar({
  density,
  onDensityChange,
  sortOptions,
  sort,
  onSortChange,
  groupOptions = [],
  groupBy,
  onGroupByChange,
  filterCount,
  onToggleFilterPanel,
  totalCount,
  filteredCount,
  selectedCount = 0,
  onSelectAllFiltered,
  layoutMode = 'table',
  onLayoutModeChange,
  showChart = false,
  onToggleChart,
  onManageReports,
  columns = [],
  visibleColumns = [],
  onToggleColumn,
  onReorderColumns,
  itemLabel = 'item',
}: EntityListToolbarProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [viewSheetOpen, setViewSheetOpen] = useState(false);
  const isPhone = useMobileExperience();

  const closeAll = useCallback(() => {
    setSortOpen(false);
    setGroupOpen(false);
    setColumnsOpen(false);
  }, []);

  const plural = pluralizeEntityLabel(itemLabel, filteredCount);

  const filtersButton = (
    <ToolbarButton
      active={filterCount > 0}
      onClick={onToggleFilterPanel}
      icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
      label="Filters"
      badge={filterCount > 0 ? filterCount : undefined}
    />
  );

  const sortLabel = sortOptions.find((o) => o.field === sort.field)?.label ?? 'Sort';
  const sortControl = (
    <div className="relative">
      <ToolbarButton
        onClick={() => {
          closeAll();
          setSortOpen(!sortOpen);
        }}
        icon={<ArrowUpDown className="h-3.5 w-3.5" />}
        // A phone row has no room for "Recently Updated"; the list names the choice.
        label={isPhone ? 'Sort' : sortLabel}
        ariaLabel={isPhone ? `Sort: ${sortLabel}` : undefined}
        hasDropdown
      />
      {sortOpen && (
        <Dropdown onClose={() => setSortOpen(false)}>
          <div className="py-1">
            <div className="px-3 py-1.5 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">
              Sort by
            </div>
            {sortOptions.map((opt) => (
              <DropdownItem
                key={opt.field}
                label={opt.label}
                active={sort.field === opt.field}
                suffix={
                  sort.field === opt.field
                    ? sort.direction === 'asc'
                      ? '↑ Asc'
                      : '↓ Desc'
                    : undefined
                }
                onClick={() => {
                  const direction =
                    sort.field === opt.field
                      ? sort.direction === 'asc'
                        ? 'desc'
                        : 'asc'
                      : 'asc';
                  onSortChange({ field: opt.field, direction });
                  setSortOpen(false);
                }}
              />
            ))}
          </div>
        </Dropdown>
      )}
    </div>
  );

  const countLabel = (
    <span className="text-xs text-[var(--text-ter)] tabular-nums whitespace-nowrap">
      {selectedCount > 0 ? (
        <>
          <span className="text-[var(--text-ter)] font-semibold">{selectedCount}</span> selected
        </>
      ) : filteredCount === totalCount ? (
        <>
          {totalCount} {plural}
        </>
      ) : (
        <>
          <span className="font-semibold text-[var(--text-sec)]">{filteredCount}</span> of {totalCount}
        </>
      )}
    </span>
  );

  // The phone row's count: numbers only ("997/1000"), the noun and "of" left out.
  const phoneCountLabel = (
    <span
      className="min-w-0 truncate text-xs text-[var(--text-ter)] tabular-nums whitespace-nowrap"
      aria-label={
        selectedCount > 0
          ? `${selectedCount} selected`
          : filteredCount === totalCount
            ? `${totalCount} ${plural}`
            : `${filteredCount} of ${totalCount} ${pluralizeEntityLabel(itemLabel, totalCount)}`
      }
    >
      {selectedCount > 0 ? (
        <>
          <span className="font-semibold">{selectedCount}</span> sel.
        </>
      ) : filteredCount === totalCount ? (
        totalCount
      ) : (
        <>
          <span className="font-semibold text-[var(--text-sec)]">{filteredCount}</span>/{totalCount}
        </>
      )}
    </span>
  );

  const showSelectAll = selectedCount === 0 && filteredCount > 0 && !!onSelectAllFiltered;

  const columnsControl =
    columns.length > 0 && onToggleColumn ? (
      <ColumnsControl
        open={columnsOpen}
        onOpenChange={(next) => {
          closeAll();
          setColumnsOpen(next);
        }}
        columns={columns}
        visibleColumns={visibleColumns}
        onToggleColumn={onToggleColumn}
        onReorderColumns={onReorderColumns ?? (() => {})}
      />
    ) : null;

  if (isPhone) {
    // A grouping, a summary or a non-default density is a view choice in force:
    // the View button says so, as Filters does for a filter.
    const viewChanged = !!groupBy || showChart || density !== 'default';
    return (
      <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] flex-shrink-0">
        {/* Fits 375 px: Filters, Sort, the count and an icon-only View (audit #46). */}
        <div className="flex min-w-0 items-center gap-2 px-3 py-2" data-testid="entity-list-toolbar-phone-row">
          {filtersButton}
          {sortControl}
          <div className="flex-1" />
          {phoneCountLabel}
          <ToolbarButton
            active={viewChanged}
            onClick={() => {
              closeAll();
              setViewSheetOpen(true);
            }}
            icon={<Settings2 className="h-4 w-4" />}
            label="View"
            iconOnly
          />
        </div>

        <BottomSheet open={viewSheetOpen} onClose={() => setViewSheetOpen(false)} title="View">
          <div className="space-y-5 pb-2">
            {showSelectAll && (
              <button
                type="button"
                onClick={() => {
                  onSelectAllFiltered?.();
                  setViewSheetOpen(false);
                }}
                className="flex min-h-[44px] w-full items-center gap-2 border border-[var(--rule)] px-3 text-sm text-[var(--text-sec)] active:bg-[var(--hover-tint)]"
              >
                <CheckSquare className="h-4 w-4" />
                Select all {filteredCount} {plural}
              </button>
            )}

            {groupOptions.length > 0 && (
              <SheetGroup label="Group by">
                <DropdownItem label="No grouping" active={!groupBy} onClick={() => onGroupByChange(null)} />
                {groupOptions.map((opt) => (
                  <DropdownItem
                    key={opt.field}
                    label={opt.label}
                    active={groupBy === opt.field}
                    onClick={() => onGroupByChange(opt.field)}
                  />
                ))}
              </SheetGroup>
            )}

            {onLayoutModeChange && (
              <SheetGroup label="Layout">
                <SegmentRow>
                  <SegmentButton active={layoutMode === 'table'} onClick={() => onLayoutModeChange('table')}>
                    <Table2 className="h-4 w-4" /> Table
                  </SegmentButton>
                  <SegmentButton active={layoutMode === 'card'} onClick={() => onLayoutModeChange('card')}>
                    <LayoutDashboard className="h-4 w-4" /> Cards
                  </SegmentButton>
                </SegmentRow>
              </SheetGroup>
            )}

            <SheetGroup label="Density">
              <SegmentRow>
                {DENSITY_MODES.map((mode) => {
                  const Icon = DENSITY_ICON[mode];
                  return (
                    <SegmentButton key={mode} active={density === mode} onClick={() => onDensityChange(mode)}>
                      <Icon className="h-4 w-4" /> {densityLabel(mode)}
                    </SegmentButton>
                  );
                })}
              </SegmentRow>
            </SheetGroup>

            {(onToggleChart || onManageReports || (layoutMode === 'table' && columnsControl)) && (
              <SheetGroup label="More">
                {onToggleChart && (
                  <DropdownItem
                    label={showChart ? 'Hide summary' : 'Show summary'}
                    active={showChart}
                    onClick={onToggleChart}
                  />
                )}
                {onManageReports && (
                  <DropdownItem
                    label="Reports"
                    onClick={() => {
                      setViewSheetOpen(false);
                      onManageReports();
                    }}
                  />
                )}
                {/* Columns are a table's; cards have none. */}
                {layoutMode === 'table' && columnsControl && <div className="px-1 py-1">{columnsControl}</div>}
              </SheetGroup>
            )}
          </div>
        </BottomSheet>
      </div>
    );
  }

  return (
    <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] flex-shrink-0">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2">
        {filtersButton}

        {sortControl}

        {groupOptions.length > 0 && (
          <div className="relative">
            <ToolbarButton
              active={!!groupBy}
              onClick={() => {
                closeAll();
                setGroupOpen(!groupOpen);
              }}
              icon={<Layers className="h-3.5 w-3.5" />}
              label={groupBy ? groupOptions.find((o) => o.field === groupBy)?.label ?? 'Group' : 'Group'}
              hasDropdown
            />
            {groupOpen && (
              <Dropdown onClose={() => setGroupOpen(false)}>
                <div className="py-1">
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">
                    Group by
                  </div>
                  <DropdownItem
                    label="No grouping"
                    active={!groupBy}
                    onClick={() => {
                      onGroupByChange(null);
                      setGroupOpen(false);
                    }}
                  />
                  <div className="my-1 mx-3 border-t border-[var(--rule)]" />
                  {groupOptions.map((opt) => (
                    <DropdownItem
                      key={opt.field}
                      label={opt.label}
                      active={groupBy === opt.field}
                      onClick={() => {
                        onGroupByChange(opt.field);
                        setGroupOpen(false);
                      }}
                    />
                  ))}
                </div>
              </Dropdown>
            )}
          </div>
        )}

        <div className="flex-1" />

        <div className="flex flex-wrap items-center gap-2">
          {countLabel}
          {showSelectAll && (
            <button
              type="button"
              onClick={onSelectAllFiltered}
              className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              <CheckSquare className="h-3 w-3" />
              Select all
            </button>
          )}
        </div>

        <div className="w-px h-6 bg-[var(--hover-tint)] mx-1" />

        {onToggleChart && (
          <button
            type="button"
            onClick={onToggleChart}
            className={`p-1.5  transition-all duration-150 ${
              showChart ? 'text-[var(--text-sec)] bg-[var(--slate-dim)]' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--surface)]'
            }`}
            title={showChart ? 'Hide summary' : 'Show summary'}
          >
            <BarChart3 className="h-4 w-4" />
          </button>
        )}

        {onLayoutModeChange && (
          <div className="flex items-center border border-[var(--rule)] overflow-hidden">
            <button
              type="button"
              onClick={() => onLayoutModeChange('table')}
              className={`p-1.5 transition-all duration-150 ${
                layoutMode === 'table'
                  ? 'text-[var(--text-sec)] bg-[var(--slate-dim)]'
                  : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--surface)]'
              }`}
              title="Table view"
            >
              <Table2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onLayoutModeChange('card')}
              className={`p-1.5 transition-all duration-150 ${
                layoutMode === 'card'
                  ? 'text-[var(--text-sec)] bg-[var(--slate-dim)]'
                  : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--surface)]'
              }`}
              title="Card view"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {onManageReports && (
          <button
            type="button"
            onClick={onManageReports}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <FileText className="h-3.5 w-3.5" />
            Reports
          </button>
        )}

        {columnsControl}

        <div className="flex items-center bg-[var(--hover-tint)]/80 p-0.5">
          {DENSITY_MODES.map((mode) => {
            const Icon = DENSITY_ICON[mode];
            const isActive = density === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => onDensityChange(mode)}
                className={`p-1  transition-all duration-150 ${
                  isActive ? 'bg-[var(--bg-raised)] text-[var(--text-sec)] shadow-sm' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                }`}
                title={densityLabel(mode)}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  icon,
  label,
  badge,
  hasDropdown,
  iconOnly = false,
  ariaLabel,
}: {
  active?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  hasDropdown?: boolean;
  /** Draw the icon alone; `label` becomes the accessible name. */
  iconOnly?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? (iconOnly ? label : undefined)}
      title={iconOnly ? label : undefined}
      className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap ${iconOnly ? 'p-1.5' : 'px-2.5 py-1.5'} text-xs font-medium  border transition-all duration-150 ${
        active
          ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] shadow-sm shadow-indigo-100'
          : 'border-[var(--rule)]/80 text-[var(--text-sec)] hover:bg-[var(--surface)] hover:border-[var(--rule)]'
      }`}
    >
      {icon}
      {!iconOnly && <span>{label}</span>}
      {badge !== undefined && (
        <span className="min-w-[1.125rem] h-[1.125rem] flex items-center justify-center rounded-full bg-[var(--slate)] text-white text-[10px] font-bold px-1 -mr-0.5">
          {badge}
        </span>
      )}
      {hasDropdown && <ChevronDown className="h-3 w-3 text-[var(--text-ter)] -mr-0.5" />}
    </button>
  );
}

function Dropdown({
  children,
  onClose,
  align = 'left',
}: {
  children: React.ReactNode;
  onClose: () => void;
  align?: 'left' | 'right';
}) {
  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div
        className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 w-52 bg-[var(--bg-raised)]  shadow-lg shadow-gray-200/50 border border-[var(--rule)]/80 z-20 overflow-hidden`}
      >
        {children}
      </div>
    </>
  );
}

function DropdownItem({
  label,
  active,
  suffix,
  onClick,
}: {
  label: string;
  active?: boolean;
  suffix?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${
        active ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium' : 'text-[var(--text-sec)] hover:bg-[var(--surface)]'
      }`}
    >
      {label}
      {suffix && <span className="text-[10px] text-[var(--text-ter)]">{suffix}</span>}
    </button>
  );
}

/** A labelled block in the phone's View sheet. */
function SheetGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-1 pb-1.5 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">{label}</div>
      <div className="border border-[var(--rule)] divide-y divide-[var(--rule)]">{children}</div>
    </div>
  );
}

function SegmentRow({ children }: { children: React.ReactNode }) {
  return <div className="flex divide-x divide-[var(--rule)]">{children}</div>;
}

function SegmentButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex min-h-[44px] flex-1 items-center justify-center gap-1.5 text-sm transition-colors ${
        active ? 'bg-[var(--slate-dim)] font-medium text-[var(--text-sec)]' : 'text-[var(--text-ter)] active:bg-[var(--hover-tint)]'
      }`}
    >
      {children}
    </button>
  );
}
